'use client';

import { useEffect, useRef } from 'react';
import { usePathname, 
  // useSearchParams 
} from 'next/navigation';
import { isHomePage } from '@/utils/page';
import { usePageStore } from '@/store/usePageStore';
import { TLangIso, TLocale } from '@/types/global';

export const PageStoreInitializer = ({
  initialIsUserLogged,
  initialLang,
  initialLangIso = 'en-US',
  gsapInitialTimeout = 0,
}: {
  initialIsUserLogged: boolean;
  initialLang: TLocale;
  initialLangIso?: TLangIso;
  gsapInitialTimeout?: number;
}) => {
  const setIsUserLogged = usePageStore((s) => s.setIsUserLogged);
  const setLocale = usePageStore((s) => s.setLocale);
  const setLangIso = usePageStore((s) => s.setLangIso);
  const setProjectBreadcrumbPath = usePageStore((s) => s.setProjectBreadcrumbPath);
  const setIsPreviousPageWork = usePageStore((s) => s.setIsPreviousPageWork);
  const setGsapTimeout = usePageStore((s) => s.setGsapTimeout);

  const previousPathRef = useRef<string | null>(null);
  const path = usePathname();
  const params = '';
  const paramsStr = params?.toString() ?? '';
  const searchParams = paramsStr ? `?${paramsStr}` : '';
  const fullPath = path + searchParams;

  useEffect(() => {
    setIsUserLogged(initialIsUserLogged);
    setLocale(initialLang);
    setLangIso(initialLangIso);
    setGsapTimeout(gsapInitialTimeout);
  }, [
    ,
    initialIsUserLogged,
    initialLang,
    initialLangIso,
    gsapInitialTimeout,
    setGsapTimeout,
    setIsUserLogged,
    setLocale,
    setLangIso,
  ]);

  useEffect(() => {
    const workPaths = [
      '/our-work',
      '/en/our-work',
      '/es/our-work',
      '/presentation',
      '/en/presentation',
      '/es/presentation',
      '/our-work/branding',
      '/en/our-work/branding',
      '/es/our-work/branding',
      '/our-work/design-consulting',
      '/en/our-work/design-consulting',
      '/es/our-work/design-consulting',
      '/our-work/visualization',
      '/en/our-work/visualization',
      '/es/our-work/visualization',
    ];

    const isWorkPage = (path: string) =>
      workPaths.includes(path) ||
      path.startsWith('/presentation?') ||
      path.startsWith('/en/presentation?') ||
      path.startsWith('/es/presentation?');

    if (previousPathRef.current) {
      const wasWork = isWorkPage(previousPathRef.current);
      setIsPreviousPageWork(wasWork);
    }

    const isWork = isWorkPage(fullPath);
    const newSlug = isHomePage(fullPath) || isWork ? fullPath : null;

    if (newSlug) {
      setProjectBreadcrumbPath(newSlug);
    }

    previousPathRef.current = fullPath;
    setGsapTimeout(200);
  }, [fullPath, setGsapTimeout, setIsPreviousPageWork, setProjectBreadcrumbPath]);

  return null;
};
